import React, { useMemo } from "react";
import { useT, Namespace } from "@racoon/i18n";
import { FilterBlock, FilterStateItem, FilterFilterParam, Button } from "@racoon/ui";
import { ConnectProto } from "@racoon/api";
import { CONTAINER_TABS, isContainerTab, type ContainerTab } from "../../model/useContainerUrl";
import { useListContainersQuery } from "~/shared/api/container/container.grpc";
import { tabCounts, type ContainerServerFilters } from "../../model/container.filters";

// The badges come from a request of their own: one row, plus the two facets `tabCounts` reads.
const COUNTS_LIST_FILTER = { currentPage: 0, pageSize: 1, facets: ["status", "last_event"] };

interface ContainerStatusFilterProps {
  activeTab: ContainerTab;
  onTabChange: (tab: ContainerTab) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  /** The filters the table's ListContainers request carries; undefined skips the counts. */
  countFilters?: ContainerServerFilters;
  /** The filter-sheet selects (type, status, site, driver); built by the parent, which owns the data. */
  filterParams?: FilterFilterParam[];
  onFilterParamsChange?: (key: string, value: string | null) => void;
  onCreateClick: () => void;
}

export const ContainerStatusFilter: React.FC<ContainerStatusFilterProps> = ({
  activeTab,
  onTabChange,
  searchValue,
  onSearchChange,
  countFilters,
  filterParams,
  onFilterParamsChange,
  onCreateClick,
}) => {
  const t = useT();

  // Every filter the table applies server-side, the driver selection included,
  // so a badge counts the rows the tab would actually show. Each facet ignores
  // its own filter: `statuses: [ACTIVE]` narrows only "last_event", so one
  // response feeds all four tabs. `lastEvents` stays off — it would narrow
  // "status". `data` keeps the last counts while new ones load, so the badges do
  // not blink out.
  const { data: countsData } = useListContainersQuery(
    {
      ...countFilters,
      statuses: [ConnectProto.ContainerStatus.ACTIVE],
      listFilter: COUNTS_LIST_FILTER,
    },
    { skip: !countFilters },
  );

  const countByTab = useMemo(() => tabCounts(countsData?.listInfo), [countsData]);

  const states: FilterStateItem[] = useMemo(
    () =>
      CONTAINER_TABS.map((tab) => ({
        key: t(Namespace.Settings, `inventory.tabs.${tab}`),
        value: tab,
        count: countByTab[tab],
      })),
    [t, countByTab],
  );

  const selectedState = useMemo(
    () => states.find((s) => s.value === activeTab) ?? states[0],
    [states, activeTab],
  );

  const handleStateChange = (state: FilterStateItem) => {
    onTabChange(isContainerTab(state.value) ? state.value : "active");
  };

  return (
    <div className="flex items-center justify-between">
      <FilterBlock
        states={states}
        statesVariant="tab"
        selectedState={selectedState}
        onStateChange={handleStateChange}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        filterParams={filterParams}
        onFilterParamsChange={onFilterParamsChange}
        direction="column"
        bgColor="white"
        additionalButtonsPosition="bottom"
        additionalButtons={
          <div className="flex gap-2 flex-nowrap shrink-0">
            <Button size="sm" variant="opacityBlueWithOutline" onClick={onCreateClick}>
              {t(Namespace.Settings, "inventory.buttons.addContainer")}
            </Button>
          </div>
        }
      />
    </div>
  );
};
